import {
  forwardRef, memo, useEffect, useState, useMemo, useCallback,
} from 'react';
import type { ReactNode } from 'react';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';

import { prepareSelectOptions } from './utils';
import { ColorSwatch } from './colorSwatch';
import type { SelectOption, SelectChangeEvent } from './select.types';

// Описание компонента подробнее:
// https://react-select.com/home#animated-components

const animatedComponents = makeAnimated();

// Стабильная ссылка для дефолта data (Rule 3 skill remove-defaultprops)
const emptyData: unknown[] = [];

// react-select сам по себе не обёрнут в React.memo: даже при ссылочно-стабильных пропсах
// родителя он перерисовывался на каждый рендер SimpleMultiSelectInput. memo-обёртка
// позволяет реально пропустить рендер, когда все пропсы стабильны.
// ВАЖНО: обёртка обязана жить на уровне модуля (не внутри компонента) — иначе каждый
// рендер порождал бы новый тип и React размонтировал бы селект (потеря фокуса/меню/значения).
// ref проходит сквозь memo транзитом: Select — forwardRef-компонент, а внутреннее состояние
// react-select (открытое меню, введённый текст) мемоизацией не блокируется.
const MemoizedSelect = memo(Select);

// Чистая функция без замыканий — на уровне модуля даёт стабильную ссылку для isOptionDisabled.
const isOptionDisabled = (opt: any) => opt.disabled;

/**
 * Пропсы «глупого» мультиселекта (без Formik).
 *
 * Наследует нативные пропсы react-select (placeholder, menuPlacement, className, isMulti, ...)
 * через `...rest`. Через `Omit` убраны только ключи с конфликтующей сигнатурой —
 * `value` и `onChange`: у нас своя семантика (массив значений, наш SelectChangeEvent).
 * Остальные нативные пропсы (включая `isMulti` — фиксируется внутри, но пропускается
 * для совместимости с потребителями, которые его передают) протекают к потребителю как есть.
 *
 * Formik-обёртка — MultiSelectInput (MR-5).
 */
export interface SimpleMultiSelectInputProps
  extends Omit<React.ComponentProps<typeof Select>, 'value' | 'onChange'> {
  name?: string;
  controlId?: string;
  label?: ReactNode;
  children?: ReactNode;
  error?: string;
  required?: boolean;
  disabled?: boolean; // наш алиас (→ isDisabled внутри)
  loading?: boolean; // наш алиас (→ isLoading внутри)
  isNextField?: boolean;
  type?: string; // 'hidden' скрывает текст ошибки
  /**
   * Класс для корневого Form.Group (например, 'mb-0' при размещении инпута в ячейке
   * таблицы). Аналог rootClassName у SimpleStringInput/SimpleSelectInput.
   */
  rootClassName?: string;
  value?: unknown[] | object;
  onChange?: (e: SelectChangeEvent) => void;
  /** Массив с данными для options (prepareSelectOptions превратит в SelectOption[]) */
  data?: unknown[];
  /**
   * Какое поле элемента массива данных использовать в качестве заголовка опции.
   * Строка (имя поля) или функция(item).
   */
  dataTitle?: string | ((item: any) => unknown);
  /**
   * Какое поле элемента массива данных использовать в качестве значения опции.
   * Строка (имя поля) или функция(item).
   */
  dataValue?: string | ((item: any) => unknown);
  /**
   * Сортировать ли options по значению.
   * @see https://developer.mozilla.org/ru/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
   */
  sort?: boolean | ((...args: any[]) => unknown);
  /**
   * @param options - все доступные опции в списке
   * @param values - значение поля из формы
   * Функция, которая возвращает массив с выбранными опциями.
   */
  mapFieldToSelectedOptions?: (options: SelectOption[], values: unknown) => SelectOption[];
  /** z-index для выпадающего списка (иногда скрывается под другими элементами) */
  dropDowndzIndex?: number;
  /**
   * Конвертировать ли примитивы (строки/числа) в объекты {value, label} для react-select.
   * По умолчанию: false (старое поведение — значения уже в формате объектов).
   * Установите true, если передаёте массив строк/чисел и хотите автоматическую конвертацию.
   * Подробное описание — в MultiSelectInput (Formik-обёртка).
   */
  convertPrimitives?: boolean;
  /**
   * Включает отображение цветовых индикаторов (квадрат/круг) рядом с опциями
   * в выпадающем списке. Если не указан (undefined), индикация включается
   * автоматически, когда хотя бы один объект в data имеет поле color
   * с непустым значением. Явное false — полностью отключает индикаторы.
   * Механизм и семантика — 1:1 как в SimpleSelectInput (там родился паттерн).
   * В выбранных значениях-«чипах» индикатор не показывается: react-select
   * рендерит их компонентом MultiValue, а не formatOptionLabel.
   */
  showColorIndicator?: boolean;
  /**
   * Форма цветового индикатора: 'square' (квадрат) или 'circle' (круг).
   * По умолчанию 'circle' — как в SimpleSelectInput.
   */
  indicatorShape?: 'square' | 'circle';
}

const SimpleMultiSelectInput = forwardRef<HTMLElement, SimpleMultiSelectInputProps>((props, ref) => {
  const [currentValue, setCurrentValue] = useState<SelectOption[]>([]);
  const [options, setOptions] = useState<SelectOption[]>([]);

  const {
    type,
    name,
    value: values,
    data = emptyData,
    dataTitle,
    dataValue,
    sort,
    // className,
    label,
    required,
    onChange,
    controlId,
    children,
    error,
    mapFieldToSelectedOptions,
    disabled,
    dropDowndzIndex = 1,
    convertPrimitives = false, // ✅ конвертировать примитивы ↔ объекты (раньше было только объекты)
    loading,
    isNextField,
    rootClassName,
    showColorIndicator: forceShowColorIndicator = undefined,
    indicatorShape = 'circle',
    // c апреля 2026 года сделал поддержку и для массивов примитивов
    // в MultiSelectInput описал подробнее это свойство и как оно работает
    // formatOptionLabel достаём из rest явно: наш цветовой рендер (когда включён)
    // обязан перебивать consumer-ский, а когда выключен — не мешать ему (см. ниже).
    formatOptionLabel: restFormatOptionLabel,
    ...rest
  } = props;

  useEffect(() => {
    const listOptions = prepareSelectOptions(data, dataTitle, dataValue, sort);
    if (listOptions) {
      setOptions(listOptions);
    }
  }, [name, data, dataTitle, dataValue, sort]);

  // ИСПРАВЛЕНИЕ 1: Конвертация строк из Formik в объекты для react-select
  useEffect(() => {
    let selectedValues = [];
    if (values) {
      if (typeof mapFieldToSelectedOptions === 'function') {
        selectedValues = mapFieldToSelectedOptions(options, values);
      } else if (convertPrimitives && Array.isArray(values)) {
        // Конвертируем массив строк/чисел в массив объектов {value, label}
        selectedValues = values.map(val => 
          options.find(opt => String(opt.value) === String(val))
        ).filter(Boolean); // Убираем undefined, если опция не найдена
      } else {
        // Старый режим: значения уже в формате объектов (или одиночный объект)
        selectedValues = Array.isArray(values) ? values : [values];
      }
    }
    setCurrentValue(selectedValues);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataValue, values, options, convertPrimitives]);

  // ИСПРАВЛЕНИЕ 2: При изменении передаем в Formik массив строк, а не объектов.
  // useCallback — чтобы ссылка была стабильной между рендерами и мемоизация
  // MemoizedSelect (см. ниже) реально пропускала рендер.
  const handleChange = useCallback((selectedOptions) => {
    setCurrentValue(selectedOptions || []);

    if (typeof onChange === 'function') {
      let outputValue = selectedOptions;

      if (convertPrimitives && Array.isArray(selectedOptions)) {
        // Конвертируем объекты обратно в массив примитивов (для Formik)
        outputValue = selectedOptions.map(opt => opt.value);
      }
      // В старом режиме передаём объекты как есть (как было раньше)

      onChange({
        target: {
          name,
          value: outputValue,
        },
      });
    }
  }, [convertPrimitives, name, onChange]);
   
  // Стабильный объект стилей: зависит только от dropDowndzIndex. Иначе пересоздавался
  // каждый рендер и ломал мемоизацию MemoizedSelect.
  const menuPortalStyles = useMemo(() => ({
    menuPortal: (provided) => ({
      ...provided,
      zIndex: dropDowndzIndex,
    }),
  }), [dropDowndzIndex]);

  // Автоопределение цветов — семантика 1:1 как в SimpleSelectInput: включены,
  // если явно не выключены и хотя бы один элемент data несёт непустое поле color.
  const hasAnyColor = useMemo(() => {
    if (forceShowColorIndicator === true) return true;
    if (forceShowColorIndicator === false) return false;
    return Array.isArray(data) && data.some((item: any) => item?.color && typeof item.color === 'string' && item.color.trim() !== '');
  }, [data, forceShowColorIndicator]);

  // Цветовой рендер опции выпадающего списка: свотч + название. Контекст 'value'
  // (выбранные чипы) не трогаем — их рендерит react-select компонентом MultiValue,
  // и formatOptionLabel на них не влияет. Мемоизация обязательна: форматтер входит
  // в пропсы MemoizedSelect, новая ссылка на каждый рендер ломала бы мемоизацию.
  // label намеренно без типа (как в SimpleSelectInput): SelectOption.label — unknown,
  // а react-select рендерит его как есть.
  const colorFormatOptionLabel = useCallback(({ label, color }: any) => (
    <div style={ { display: 'flex', alignItems: 'center' } }>
      <ColorSwatch color={ color } shape={ indicatorShape } />
      <span>{ label }</span>
    </div>
  ), [indicatorShape]);

  // Наш форматтер действует только при включённых индикаторах; иначе дорога
  // consumer-скому из rest (если он есть), как до появления механизма.
  const finalFormatOptionLabel = hasAnyColor ? colorFormatOptionLabel : restFormatOptionLabel;

  const input = (
    <MemoizedSelect
      ref={ ref as any } // forwardRef<HTMLElement> → react-select ждёт ref на экземпляр Select; runtime идентичен, типы разойдутся. ref транзитом проходит сквозь memo-обёртку.
      name={ name }
      options={ options }
      // B3: единый источник правды — currentValue (state). Раньше здесь был двойной источник:
      // convertPrimitives брал currentValue, а прочие режимы пересчитывали selectMultiValues(options, values)
      // на каждом рендере. Но currentValue уже корректно формируется useEffect'ом выше для ВСЕХ режимов
      // (mapFieldToSelectedOptions / convertPrimitives / объекты как есть), поэтому отдельный пересчёт тут избыточен.
      value={ currentValue }
      components={ animatedComponents }
      closeMenuOnSelect={ false }
      onChange={ handleChange }
      isOptionDisabled={ isOptionDisabled }
      isMulti
      isDisabled={ disabled || loading }
      isLoading={ loading }
      { ...rest }
      formatOptionLabel={ finalFormatOptionLabel }
      className='react-select-container'
      classNamePrefix='react-select'
      menuPortalTarget={ document.body }
      styles={ menuPortalStyles }
    />
  );

  return (
    <Form.Group controlId={ controlId } className={ `${ rootClassName ?? '' }${ error ? ' field-has-error' : '' }${ isNextField && !error && !disabled ? ' field-is-next' : '' }`.trim() || undefined }>
      { label && (
        <Form.Label className={ required ? 'required' : null } style= { { zIndex:'20' } }>
          { label }
        </Form.Label>
      ) }
      { children ? (
        <InputGroup>
          { input }
          { children }
        </InputGroup>
      ) : input }
      { error && (type !== 'hidden') && (
        <small className='form-text text-danger'>
          { error }
        </small>
      ) }
    </Form.Group>
  );
});

SimpleMultiSelectInput.displayName = 'SimpleMultiSelectInput';

export default SimpleMultiSelectInput;